import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  Users,
  FileText,
  BookUser,
  Settings,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  Target,
  FolderKanban,
  Banknote,
  Send,
  Globe,
} from "lucide-react";
import { useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import logoImage from "@assets/png_1768006856399.png";
import { useAuth } from "@/lib/auth";

interface DashboardStats {
  buyersCount: number;
  clientsCount: number;
  prospectsCount: number;
  coiCount: number;
  capitalProvidersCount: number;
}

interface NavItem {
  name: string;
  href: string;
  icon: any;
  section?: string;
}

const navigationItems: NavItem[] = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard, section: "Core" },
  { name: "Buyer Network", href: "/buyers", icon: Building2, section: "Entities" },
  { name: "Clients", href: "/clients", icon: Briefcase, section: "Entities" },
  { name: "Prospects", href: "/prospects", icon: Target, section: "Entities" },
  { name: "COI", href: "/coi", icon: Users, section: "Entities" },
  { name: "Capital Providers", href: "/capital-providers", icon: Banknote, section: "Entities" },
  { name: "All Contacts", href: "/contacts", icon: BookUser, section: "Entities" },
  { name: "Projects", href: "/projects", icon: FolderKanban, section: "Tools" },
  { name: "Campaigns", href: "/campaigns", icon: Send, section: "Tools" },
  { name: "Website", href: "/website", icon: Globe, section: "Tools" },
  { name: "Tasks", href: "/tasks", icon: CheckCircle, section: "Tools" },
  // Last in Tools, and scrolls with everything else. It used to be pinned in
  // the footer, which froze it against the viewport while the nav moved.
  { name: "Settings", href: "/settings", icon: Settings, section: "Tools" },
];

export function Sidebar({ collapsed = false, onToggle }: { collapsed?: boolean; onToggle?: () => void }) {
  const [location] = useLocation();
  const { user } = useAuth();

  const { data: entityStats } = useQuery<DashboardStats>({
    queryKey: ["dashboard", "stats"],
    queryFn: async () => {
      const res = await fetch("/api/dashboard/stats", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: !collapsed,
  });

  const navBadgeCounts: Record<string, number | undefined> = {};

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-screen bg-sidebar text-sidebar-foreground flex-col transition-all duration-300 z-50 hidden md:flex",
        collapsed ? "w-[72px]" : "w-[260px]"
      )}
      data-testid="sidebar"
    >
      <div className={cn("flex items-center border-b border-sidebar-border relative", collapsed ? "h-20 justify-center px-2" : "h-32 px-2")}>
        {!collapsed && (
          <div className="flex items-center justify-center flex-1">
            <img 
              src={logoImage} 
              alt="First Turn Capital" 
              className="w-[220px] h-auto object-contain"
            />
          </div>
        )}
        {collapsed && (
          <div className="w-10 h-10 rounded-lg gold-gradient flex items-center justify-center">
            <span className="text-xs font-bold text-white">FTC</span>
          </div>
        )}
        <button
          onClick={onToggle}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-expanded={!collapsed}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-sidebar-accent transition-colors z-10"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-sidebar-foreground" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-sidebar-foreground" />
          )}
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {(() => {
          let currentSection = "";
          return navigationItems.map((item, index) => {
            const isActive = location === item.href || (item.href !== "/" && location.startsWith(item.href));
            const Icon = item.icon;
            const showSectionLabel = !collapsed && item.section && item.section !== currentSection;
            if (item.section) currentSection = item.section;

            const badgeCount = !collapsed ? navBadgeCounts[item.name] : undefined;

            const linkContent = (
              <Link
                key={item.name}
                href={item.href}
                aria-label={item.name}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group",
                  isActive
                    ? "sidebar-active-enhanced text-white font-semibold"
                    : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-white"
                )}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className={cn("w-5 h-5 flex-shrink-0", isActive ? "text-primary" : "text-sidebar-foreground/70 group-hover:text-white")} />
                {!collapsed && (
                  <span className="text-sm truncate flex-1">{item.name}</span>
                )}
                {badgeCount != null && badgeCount > 0 && (
                  <span className="ml-auto text-xs bg-primary/15 text-primary font-medium px-1.5 py-0.5 rounded-full tabular-nums">
                    {badgeCount}
                  </span>
                )}
              </Link>
            );

            const wrappedLink = collapsed ? (
              <Tooltip key={item.name} delayDuration={0}>
                <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                <TooltipContent side="right" className="bg-sidebar text-sidebar-foreground border-sidebar-border">
                  {item.name}
                </TooltipContent>
              </Tooltip>
            ) : linkContent;

            return (
              <div key={item.name}>
                {showSectionLabel && (
                  <div className="sidebar-section-label mt-4 first:mt-0">
                    {item.section}
                  </div>
                )}
                <div className="mt-1">
                  {wrappedLink}
                </div>
              </div>
            );
          });
        })()}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        {/* No avatar here either — see Header.tsx. Expanded, the name and email
            are the identity. Collapsed, there is no room for a name, so the
            initials stand in as plain type rather than as a circle pretending
            to be a photograph. */}
        <div className={cn(collapsed ? "flex justify-center" : "px-3")}>
          {collapsed ? (
            <span className="text-xs font-semibold tracking-wide text-sidebar-foreground/70">
              {user?.initials || "—"}
            </span>
          ) : (
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{user?.name || "User"}</p>
              <p className="truncate text-xs text-sidebar-foreground/70">{user?.email || ""}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
